/**
 * Schedule Builder API Service (Feature 004)
 * Follows the same pattern as actionsService.ts
 */
import { auth } from '../firebase';
import {
  Schedule,
  ScheduleCreate,
  ScheduleUpdate,
  ScheduleListResponse,
  ScheduleStep,
  StepCreate,
  StepUpdate,
  StepReorderRequest,
  ScheduleType,
  DayOfWeek,
  WeeklyDayUpdateRequest,
  WeeklyDayCopyRequest,
  WeeklyDayConfig,
} from '../types/schedule';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

class ScheduleService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  // ============================================================================
  // Schedule CRUD
  // ============================================================================

  /**
   * List all schedules for a device
   */
  async listSchedules(
    deviceId: string,
    options?: {
      type?: ScheduleType;
      isActive?: boolean;
    }
  ): Promise<ScheduleListResponse> {
    const params = new URLSearchParams();
    if (options?.type) {
      params.set('type', options.type);
    }
    if (options?.isActive !== undefined) {
      params.set('is_active', String(options.isActive));
    }
    const queryString = params.toString();
    const endpoint = `/devices/${deviceId}/schedules/${queryString ? `?${queryString}` : ''}`;
    return this.request<ScheduleListResponse>(endpoint);
  }

  /**
   * Get a single schedule
   */
  async getSchedule(deviceId: string, scheduleId: string): Promise<Schedule> {
    return this.request<Schedule>(`/devices/${deviceId}/schedules/${scheduleId}`);
  }

  /**
   * Create a new schedule
   */
  async createSchedule(deviceId: string, schedule: ScheduleCreate): Promise<Schedule> {
    return this.request<Schedule>(`/devices/${deviceId}/schedules/`, {
      method: 'POST',
      body: JSON.stringify(schedule),
    });
  }

  /**
   * Update an existing schedule
   */
  async updateSchedule(
    deviceId: string,
    scheduleId: string,
    updates: ScheduleUpdate
  ): Promise<Schedule> {
    return this.request<Schedule>(`/devices/${deviceId}/schedules/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a schedule
   */
  async deleteSchedule(deviceId: string, scheduleId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/devices/${deviceId}/schedules/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Duplicate a schedule with all its steps
   */
  async duplicateSchedule(deviceId: string, scheduleId: string): Promise<Schedule> {
    return this.request<Schedule>(`/devices/${deviceId}/schedules/${scheduleId}/duplicate`, {
      method: 'POST',
    });
  }

  // ============================================================================
  // Step CRUD
  // ============================================================================

  /**
   * Add a step to a schedule
   */
  async addStep(
    deviceId: string,
    scheduleId: string,
    step: StepCreate,
    atPosition?: number
  ): Promise<ScheduleStep> {
    const params = atPosition !== undefined ? `?at_position=${atPosition}` : '';
    return this.request<ScheduleStep>(
      `/devices/${deviceId}/schedules/${scheduleId}/steps${params}`,
      {
        method: 'POST',
        body: JSON.stringify(step),
      }
    );
  }

  /**
   * Update an existing step
   */
  async updateStep(
    deviceId: string,
    scheduleId: string,
    stepId: string,
    updates: StepUpdate
  ): Promise<ScheduleStep> {
    return this.request<ScheduleStep>(
      `/devices/${deviceId}/schedules/${scheduleId}/steps/${stepId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }

  /**
   * Delete a step
   */
  async deleteStep(
    deviceId: string,
    scheduleId: string,
    stepId: string
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/devices/${deviceId}/schedules/${scheduleId}/steps/${stepId}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Reorder steps in a schedule
   */
  async reorderSteps(
    deviceId: string,
    scheduleId: string,
    stepIds: string[]
  ): Promise<{ message: string }> {
    const request: StepReorderRequest = { step_ids: stepIds };
    return this.request<{ message: string }>(
      `/devices/${deviceId}/schedules/${scheduleId}/steps/reorder`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  // ============================================================================
  // Weekly Schedule Day Operations
  // ============================================================================

  /**
   * Update a day's configuration in a weekly schedule
   */
  async updateDayConfig(
    deviceId: string,
    scheduleId: string,
    day: DayOfWeek,
    updates: WeeklyDayUpdateRequest
  ): Promise<{ message: string; day: WeeklyDayConfig }> {
    return this.request<{ message: string; day: WeeklyDayConfig }>(
      `/devices/${deviceId}/schedules/${scheduleId}/days/${day}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }

  /**
   * Add a step to a specific day in a weekly schedule
   */
  async addDayStep(
    deviceId: string,
    scheduleId: string,
    day: DayOfWeek,
    step: StepCreate,
    atPosition?: number
  ): Promise<ScheduleStep> {
    const params = atPosition !== undefined ? `?at_position=${atPosition}` : '';
    return this.request<ScheduleStep>(
      `/devices/${deviceId}/schedules/${scheduleId}/days/${day}/steps${params}`,
      {
        method: 'POST',
        body: JSON.stringify(step),
      }
    );
  }

  /**
   * Update a step in a weekly schedule day
   */
  async updateDayStep(
    deviceId: string,
    scheduleId: string,
    day: DayOfWeek,
    stepId: string,
    updates: StepUpdate
  ): Promise<ScheduleStep> {
    return this.request<ScheduleStep>(
      `/devices/${deviceId}/schedules/${scheduleId}/days/${day}/steps/${stepId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }

  /**
   * Delete a step from a weekly schedule day
   */
  async deleteDayStep(
    deviceId: string,
    scheduleId: string,
    day: DayOfWeek,
    stepId: string
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/devices/${deviceId}/schedules/${scheduleId}/days/${day}/steps/${stepId}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Reorder steps within a weekly schedule day
   */
  async reorderDaySteps(
    deviceId: string,
    scheduleId: string,
    day: DayOfWeek,
    stepIds: string[]
  ): Promise<{ message: string }> {
    const request: StepReorderRequest = { step_ids: stepIds };
    return this.request<{ message: string }>(
      `/devices/${deviceId}/schedules/${scheduleId}/days/${day}/steps/reorder`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  /**
   * Copy steps from one day to another in a weekly schedule
   */
  async copyDaySteps(
    deviceId: string,
    scheduleId: string,
    sourceDay: DayOfWeek,
    targetDay: DayOfWeek,
    options: WeeklyDayCopyRequest = { clearExisting: true }
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/devices/${deviceId}/schedules/${scheduleId}/days/${targetDay}/copy-from/${sourceDay}`,
      {
        method: 'POST',
        body: JSON.stringify(options),
      }
    );
  }
}

export const scheduleService = new ScheduleService();
